import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from './common/config.service';
import { 
  AiModernizationReadinessSettings,
  AiModernizationReadinessSettingsDetail,
  AiModernizationReadinessSettingsVersionSummary,
  ModernizationReadinessJob,
  ModernizationReadinessJobLog,
  ModernizationReadinessCalculationStatus,
  LiveLogResponse
} from '../models/chat.models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ModernizationReadinessService {
  private settingsUrl: string;
  private calculateUrl: string;

  constructor(
    private http: HttpClient,
    configService: ConfigService
  ) {
    this.settingsUrl = environment.apiUrl + '/admin/modernization-readiness/settings';
    this.calculateUrl = environment.apiUrl + '/admin/modernization-readiness/calculate';
  }

  // ==================== Settings Management ====================

  /**
   * Get all modernization readiness settings versions (summary)
   */
  getAllVersions(): Observable<AiModernizationReadinessSettingsVersionSummary[]> {
    return this.http.get<AiModernizationReadinessSettingsVersionSummary[]>(`${this.settingsUrl}/versions`);
  }

  /**
   * Get active modernization readiness settings
   */
  getActiveSettings(): Observable<AiModernizationReadinessSettingsDetail> {
    return this.http.get<AiModernizationReadinessSettingsDetail>(`${this.settingsUrl}/active`);
  }

  /**
   * Get specific settings version by ID
   */
  getVersionById(id: number): Observable<AiModernizationReadinessSettingsDetail> {
    return this.http.get<AiModernizationReadinessSettingsDetail>(`${this.settingsUrl}/versions/${id}`);
  }

  /**
   * Create new settings version (there is no update - settings are immutable versions)
   */
  createSettings(settings: AiModernizationReadinessSettings): Observable<AiModernizationReadinessSettingsDetail> {
    return this.http.post<AiModernizationReadinessSettingsDetail>(this.settingsUrl, settings);
  }

  /**
   * Create a minimal settings version using only an AI connection ID and an assessment rules prompt.
   * All advanced orchestration fields fall back to built-in defaults.
   */
  createSimplifiedSettings(
    aiConnectionId: number,
    prompt: string,
    deprecatedTechnologies?: string | null
  ): Observable<AiModernizationReadinessSettingsDetail> {
    return this.http.post<AiModernizationReadinessSettingsDetail>(`${this.settingsUrl}/simplified`, {
      aiConnectionId,
      prompt,
      deprecatedTechnologies,
    });
  }

  /**
   * Activate a specific settings version (rollback)
   */
  activateVersion(id: number): Observable<any> {
    return this.http.put<any>(`${this.settingsUrl}/${id}/activate`, {});
  }

  /**
   * Delete settings version
   */
  deleteVersion(id: number): Observable<void> {
    return this.http.delete<void>(`${this.settingsUrl}/${id}`);
  }

  // ==================== Calculation Management ====================

  /**
   * Trigger modernization readiness calculation for all applications
   */
  triggerCalculation(): Observable<ModernizationReadinessJob> {
    return this.http.post<ModernizationReadinessJob>(this.calculateUrl, {});
  }

  /**
   * Trigger modernization readiness calculation for a single application (by SysId or name)
   */
  triggerCalculationForOne(query: string): Observable<any> {
    return this.http.post<any>(`${this.calculateUrl}/single`, { query });
  }

  /**
   * Pause the currently running calculation
   */
  pauseCalculation(): Observable<any> {
    return this.http.post<any>(`${this.calculateUrl}/pause`, {});
  }

  /**
   * Resume a paused calculation
   */
  resumeCalculation(): Observable<any> {
    return this.http.post<any>(`${this.calculateUrl}/resume`, {});
  }

  /**
   * Cancel the currently running calculation
   */
  cancelCalculation(): Observable<any> {
    return this.http.post<any>(`${this.calculateUrl}/cancel`, {});
  }

  /**
   * Get current calculation status
   */
  getCalculationStatus(): Observable<ModernizationReadinessCalculationStatus> {
    return this.http.get<ModernizationReadinessCalculationStatus>(`${this.calculateUrl}/status`);
  }

  /**
   * Get job history (paginated)
   */
  getJobHistory(pageNumber: number = 1, pageSize: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('page', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    
    return this.http.get<any>(`${this.calculateUrl}/history`, { params });
  }

  /**
   * Get specific job details by ID
   */
  getJobById(jobId: number): Observable<ModernizationReadinessJob> {
    return this.http.get<ModernizationReadinessJob>(`${this.calculateUrl}/job/${jobId}`);
  }

  /**
   * Get job logs for a specific job
   */
  getJobLogs(
    jobId: number, 
    pageNumber: number = 1, 
    pageSize: number = 50,
    applicationNameFilter?: string,
    resultFilter?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    
    if (applicationNameFilter) {
      params = params.set('applicationName', applicationNameFilter);
    }
    
    if (resultFilter) {
      params = params.set('result', resultFilter);
    }
    
    return this.http.get<any>(`${this.calculateUrl}/job/${jobId}/logs`, { params });
  }

  /**
   * Cancel a running job
   */
  cancelJob(jobId: number): Observable<void> {
    return this.http.post<void>(`${this.calculateUrl}/jobs/${jobId}/cancel`, {});
  }

  /**
   * Get live log entries emitted by the current calculation run.
   * Pass the last received entry ID to fetch only new entries (incremental polling).
   */
  getLiveLogs(sinceId: number = 0): Observable<LiveLogResponse> {
    const params = new HttpParams().set('sinceId', sinceId.toString());
    return this.http.get<LiveLogResponse>(`${this.calculateUrl}/logs/live`, { params });
  }
}
